import React, { useEffect, useMemo, useState } from 'react';
import './App.css';
import DecisionCockpit from './components/DecisionCockpit';
import DecisionHistory from './components/DecisionHistory';
import DecisionExecutionForm from './components/DecisionExecutionForm';
import AuthLogin from './components/AuthLogin';
import AuthBootstrap from './components/AuthBootstrap';
import { API_BASE_URL } from './config';

function App() {
  const [activeTab, setActiveTab] = useState('cockpit');
  const [lastRun, setLastRun] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [authUser, setAuthUser] = useState(null);
  const [authError, setAuthError] = useState('');
  const [pathname, setPathname] = useState(window.location.pathname);

  const isBootstrapRoute = useMemo(() => pathname === '/bootstrap', [pathname]);

  const goToPath = (path) => {
    window.history.pushState({}, '', path);
    setPathname(path);
  };

  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const refreshMe = async () => {
    setAuthLoading(true);
    setAuthError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Falha ao validar sessão');
      }

      const payload = await response.json();
      if (payload.authenticated) {
        setAuthUser(payload.user);
      } else {
        setAuthUser(null);
      }
    } catch (err) {
      setAuthError(err.message || 'Erro de autenticação');
      setAuthUser(null);
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    refreshMe();
  }, []);

  const handleRunComplete = (runData) => {
    setLastRun(runData);
    setActiveTab('cockpit');
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (_err) {
      // ignora falha de rede e força estado local de logout
    } finally {
      setAuthUser(null);
      goToPath('/');
    }
  };

  const handleUnauthorized = () => {
    setAuthUser(null);
    goToPath('/');
  };

  if (authLoading) {
    return <div className="App"><main className="App-main">Validando sessão...</main></div>;
  }

  if (!authUser) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>VASTA - Cockpit de Decisão</h1>
          <p className="subtitle">Acesso autenticado</p>
        </header>

        <main className="App-main">
          {authError && <div className="alert alert-error">{authError}</div>}
          {isBootstrapRoute ? (
            <AuthBootstrap
              onBootstrapped={(user) => {
                setAuthUser(user);
                goToPath('/');
              }}
              onCancel={() => goToPath('/')}
            />
          ) : (
            <AuthLogin
              onAuthenticated={(user) => {
                setAuthUser(user);
                goToPath('/');
              }}
              onGoBootstrap={() => goToPath('/bootstrap')}
            />
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>VASTA - Cockpit de Decisão</h1>
        <p className="subtitle">Operador autenticado: {authUser.username}</p>
        <button type="button" className="btn btn-secondary" onClick={handleLogout}>
          Logout
        </button>
      </header>

      <nav className="App-nav">
        <button
          className={activeTab === 'cockpit' ? 'active' : ''}
          onClick={() => setActiveTab('cockpit')}
        >
          Cockpit
        </button>
        <button
          className={activeTab === 'execute' ? 'active' : ''}
          onClick={() => setActiveTab('execute')}
        >
          Executar
        </button>
        <button
          className={activeTab === 'history' ? 'active' : ''}
          onClick={() => setActiveTab('history')}
        >
          Histórico
        </button>
      </nav>

      <main className="App-main">
        {isLoading ? (
          <div className="loading">Processando...</div>
        ) : (
          <>
            {activeTab === 'cockpit' && <DecisionCockpit lastRun={lastRun} />}
            {activeTab === 'execute' && (
              <DecisionExecutionForm
                onRunComplete={handleRunComplete}
                setIsLoading={setIsLoading}
                onUnauthorized={handleUnauthorized}
              />
            )}
            {activeTab === 'history' && <DecisionHistory onUnauthorized={handleUnauthorized} />}
          </>
        )}
      </main>
    </div>
  );
}

export default App;
